import {makeAutoObservable} from "mobx"
import {api, catchApi} from "../../api"
import {AuditInfoDto, CustomerDetailDto} from "../../api/cms"
import {equals} from "../../utils"
import {validationStore} from "../ValidationStore"

class CmsDetailStore {
    get tab(): string | null {
        return this._tab;
    }

    set tab(value: string | null) {
        this._tab = value;
    }

    get audit(): AuditInfoDto | undefined {
        return this._audit
    }

    set audit(value: AuditInfoDto | undefined) {
        this._audit = value
    }
    get history(): CustomerDetailDto[] {
        return this._history
    }

    set history(value: CustomerDetailDto[]) {
        this._history = value
    }
    get valid(): boolean {
        return this._valid
    }

    set valid(value: boolean) {
        this._valid = value
    }
    get saving(): boolean {
        return this._saving
    }

    set saving(value: boolean) {
        this._saving = value
    }
    get addressStreet(): string {
        return this._addressStreet
    }

    set addressStreet(value: string) {
        this._addressStreet = value
    }
    get addressPostCode(): string {
        return this._addressPostCode
    }

    set addressPostCode(value: string) {
        this._addressPostCode = value
    }
    get addressHouseNumber(): string {
        return this._addressHouseNumber
    }

    set addressHouseNumber(value: string) {
        this._addressHouseNumber = value
    }
    get addressCountry(): string {
        return this._addressCountry
    }

    set addressCountry(value: string) {
        this._addressCountry = value
    }
    get addressCity(): string {
        return this._addressCity
    }

    set addressCity(value: string) {
        this._addressCity = value
    }
    get addressAddition(): string {
        return this._addressAddition
    }

    set addressAddition(value: string) {
        this._addressAddition = value
    }
    get name(): string {
        return this._name
    }

    set name(value: string) {
        this._name = value
    }
    get displayName(): string {
        return this._displayName
    }

    set displayName(value: string) {
        this._displayName = value
    }
    get customerNumber(): string {
        return this._customerNumber
    }

    set customerNumber(value: string) {
        this._customerNumber = value
    }
    get active(): boolean {
        return this._active
    }

    set active(value: boolean) {
        this._active = value
    }
    get data(): CustomerDetailDto | undefined {
        return this._data
    }

    set data(value: CustomerDetailDto | undefined) {
        this._data = value
    }
    get id(): string {
        return this._id
    }

    set id(value: string) {
        this._id = value
    }

    get dirty(): boolean {
        const current = this.build()
        return !equals(this.data, current)
    }

    private _id: string = ''
    private _data: CustomerDetailDto | undefined
    private _audit: AuditInfoDto | undefined
    private _history: CustomerDetailDto[] = []
    private _saving: boolean = false
    private _valid: boolean = false


    private _active: boolean = false
    private _customerNumber: string = ''
    private _displayName: string = ''
    private _name: string = ''
    private _addressAddition: string = ''
    private _addressCity: string = ''
    private _addressCountry: string = ''
    private _addressHouseNumber: string = ''
    private _addressPostCode: string = ''
    private _addressStreet: string = ''
    private _tab: string | null = ''

    constructor() {
        makeAutoObservable(this)
    }

    async load(): Promise<boolean> {
        const result = await catchApi(async () => await api.cms.api.getCustomer(this.id))
        this.data = result?.data
        this.reset()
        if (result) {
            this.loadHistory().finally()
        }
        return Boolean(result)
    }

    async loadHistory() {
        const result = await catchApi(async () => await api.cms.api.getHistory(this.id))
        this.history = result?.data || []
    }

    async save() {
        const dto = this.build()
        this.saving = true
        const result = await catchApi(async () => await api.cms.api.updateCustomer(this.id, dto))
        this.data = result?.data!! as CustomerDetailDto
        this.reset()
        this.saving = false
    }

    async validate() {
        this.valid = false
        const dto = this.build()
        await validationStore.validateWithQuery(api.cms.api.validateCustomer, dto, {validateCustomerNumber: false})

        this.valid = Boolean(validationStore.valid)
    }

    public reset() {
        const {
            data,
            audit
        } = this.data || {}

        const {
            active,
            customerNumber,
            displayName,
            name,
            address
        } = data || {}

        const {
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street
        } = address || {}

        this.active = active || false
        this.customerNumber = customerNumber || ''
        this.displayName = displayName || ''
        this.name = name || ''
        this.addressCity = city || ''
        this.addressAddition = addition || ''
        this.addressCountry = country || 'DE'
        this.addressHouseNumber = houseNumber || ''
        this.addressPostCode = postCode || ''
        this.addressStreet = street || ''
        this.audit = audit
        this.data = this.build() // delete all non used fields like created date
    }

    public build(): CustomerDetailDto {
        const {
            active,
            customerNumber,
            displayName,
            name,
            addressCity,
            addressStreet,
            addressAddition,
            addressCountry,
            addressHouseNumber,
            addressPostCode,
            audit
        } = this
        return {
            audit,
            data: {
                name,
                active,
                customerNumber,
                displayName,
                address: {
                    city: addressCity,
                    addition: addressAddition,
                    country: addressCountry,
                    street: addressStreet,
                    houseNumber: addressHouseNumber,
                    postCode: addressPostCode
                }
            }
        }
    }
}

const cmsDetailStore = new CmsDetailStore()

export {
    cmsDetailStore
}
